import { Injectable } from '@angular/core';
import {
  createDefaultComparisonState,
  FUNDRAISING_PRICING_REGISTRY,
  getAvailablePricingConfigurations,
  getDefaultOptionSelections,
  getPricingConfiguration,
} from '../data/platform-pricing.config';
import { getFundraiserProfile } from '../data/fundraiser-profile.config';
import { getVersusPlatformIdsForPath } from '../data/fundraising-versus.config';
import type {
  ComparisonOptionSelections,
  FundraisingComparisonState,
  PlatformId,
} from '../models/fundraising-comparison.models';

const LEGACY_BIG_NEST_OPTIONS: Record<string, string> = {
  absorb: 'organization-absorbs',
  optional: 'optional-donor-contribution',
  'service-fee': 'automatic-service-fee',
};

@Injectable({ providedIn: 'root' })
export class UrlScenarioStateService {
  read(): FundraisingComparisonState {
    const defaults = createDefaultComparisonState();
    if (typeof window === 'undefined') return defaults;

    const params = new URLSearchParams(window.location.search);
    const version = params.get('v');
    const hasKnownUnversionedState = [
      'amount',
      'profile',
      'donors',
      'compare',
      'options',
      'model',
      'cost',
    ].some((key) => params.has(key));
    const effectiveVersion =
      version === null && hasKnownUnversionedState ? '3' : version;
    if (
      effectiveVersion !== '1' &&
      effectiveVersion !== '2' &&
      effectiveVersion !== '3'
    ) {
      return defaults;
    }

    const amountCents = this.parseDollarAmount(
      params.get('amount'),
      defaults.intendedGiftTotalCents,
    );
    const donationCountOverride = this.parseDonationCountOverride(
      params.get('donors'),
      amountCents,
    );
    const optionSelections = this.parseOptions(params.get('options'));
    this.applyPlatformAliases(params, optionSelections);
    if (effectiveVersion === '1') {
      const legacyChoice = LEGACY_BIG_NEST_OPTIONS[params.get('bn') ?? ''];
      if (legacyChoice) {
        optionSelections['big-nest'] = {
          ...optionSelections['big-nest'],
          'fee-handling': legacyChoice,
        };
      }
    }

    return {
      version: 3,
      intendedGiftTotalCents: amountCents,
      profileId: this.parseProfileId(params.get('profile')),
      donationCountOverride,
      platformIds: this.parsePlatforms(params.get('compare')),
      optionSelections,
    };
  }

  replace(state: FundraisingComparisonState): void {
    if (typeof window === 'undefined') return;
    window.history.replaceState(null, '', this.buildUrl(state));
  }

  buildAbsoluteUrl(state: FundraisingComparisonState): string {
    if (typeof window === 'undefined') return this.buildUrl(state);
    return new URL(this.buildUrl(state), window.location.origin).toString();
  }

  buildAbsoluteUrlForPath(
    state: FundraisingComparisonState,
    pathname: string,
  ): string {
    const normalizedPathname = pathname.startsWith('/')
      ? pathname
      : `/${pathname}`;
    if (typeof window === 'undefined') {
      return this.buildUrl(state, normalizedPathname);
    }
    return new URL(
      this.buildUrl(state, normalizedPathname),
      window.location.origin,
    ).toString();
  }

  private buildUrl(
    state: FundraisingComparisonState,
    targetPathname?: string,
  ): string {
    const pathname =
      targetPathname ??
      (typeof window === 'undefined'
        ? '/fundraising-platforms/'
        : window.location.pathname);
    const params = new URLSearchParams();
    params.set('v', '3');
    params.set('amount', this.formatDollarAmount(state.intendedGiftTotalCents));
    params.set('profile', state.profileId);
    if (state.donationCountOverride !== null) {
      params.set('donors', String(state.donationCountOverride));
    }
    if (
      !this.isOverviewPath(pathname) &&
      !this.matchesDefaultComparison(state.platformIds)
    ) {
      params.set('compare', state.platformIds.join(','));
    }
    const options = this.serializeOptions(
      state.optionSelections,
      this.platformIdsForPath(pathname, state.platformIds),
    );
    if (options) params.set('options', options);
    return `${pathname}?${params.toString()}`;
  }

  private parseDollarAmount(
    rawAmount: string | null,
    fallbackCents: number,
  ): number {
    if (!rawAmount || !/^\d+(?:\.\d{1,2})?$/.test(rawAmount.trim())) {
      return fallbackCents;
    }
    const [dollars, cents = ''] = rawAmount.trim().split('.');
    const parsedCents =
      Number(dollars) * 100 + Number(cents.padEnd(2, '0').slice(0, 2));
    if (!Number.isSafeInteger(parsedCents)) return fallbackCents;
    return Math.max(0, Math.min(1_000_000_000, parsedCents));
  }

  private parseDonationCountOverride(
    rawCount: string | null,
    amountCents: number,
  ): number | null {
    if (amountCents === 0 || rawCount === null) return null;
    const parsedCount = Number(rawCount);
    if (!Number.isInteger(parsedCount) || parsedCount <= 0) return null;
    return Math.max(1, Math.min(100_000, amountCents, parsedCount));
  }

  private parseProfileId(rawProfileId: string | null): string {
    return getFundraiserProfile(rawProfileId ?? undefined).id;
  }

  private parsePlatforms(rawPlatforms: string | null): PlatformId[] {
    if (rawPlatforms === null) {
      return [...FUNDRAISING_PRICING_REGISTRY.defaultComparisonPlatformIds];
    }
    const availablePlatformIds = new Set(
      getAvailablePricingConfigurations().map((platform) => platform.id),
    );
    const requested = new Set(
      rawPlatforms
        .split(',')
        .filter((id): id is PlatformId =>
          availablePlatformIds.has(id as PlatformId),
        ),
    );
    if (requested.size === 0) {
      return [...FUNDRAISING_PRICING_REGISTRY.defaultComparisonPlatformIds];
    }
    requested.add('big-nest');
    return FUNDRAISING_PRICING_REGISTRY.canonicalPlatformOrder.filter((id) =>
      requested.has(id),
    );
  }

  private parseOptions(rawOptions: string | null): ComparisonOptionSelections {
    const selections = getDefaultOptionSelections();
    for (const token of (rawOptions ?? '').split(',')) {
      const [key, choiceId] = token.split(':');
      const separator = key?.indexOf('.') ?? -1;
      if (separator < 1 || !choiceId) continue;
      const platformId = key.slice(0, separator) as PlatformId;
      const optionGroupId = key.slice(separator + 1);
      if (
        platformId === 'apex' &&
        optionGroupId === 'cost-assumption' &&
        ['30-percent', '40-percent', '50-percent'].includes(choiceId)
      ) {
        this.applyLegacyApexCost(
          selections,
          Number(choiceId.replace('-percent', '')),
        );
        continue;
      }
      const platform = getPricingConfiguration(platformId);
      const group = platform?.optionGroups.find(
        (candidate) => candidate.id === optionGroupId,
      );
      if (!group) continue;
      const normalizedChoiceId = group.input
        ? this.normalizeInputOption(group, choiceId)
        : (group.choices ?? []).some((choice) => choice.id === choiceId)
          ? choiceId
          : null;
      if (normalizedChoiceId === null) continue;
      selections[platformId] = {
        ...selections[platformId],
        [optionGroupId]: normalizedChoiceId,
      };
    }
    return selections;
  }

  private serializeOptions(
    selections: ComparisonOptionSelections,
    platformIds: PlatformId[],
  ): string {
    const tokens: string[] = [];
    const includedIds = new Set(platformIds);
    for (const platform of getAvailablePricingConfigurations().filter(
      (candidate) => includedIds.has(candidate.id),
    )) {
      const platformId = platform.id;
      for (const group of platform.optionGroups) {
        const choiceId =
          selections[platformId]?.[group.id] ?? group.defaultChoiceId;
        if (choiceId !== group.defaultChoiceId) {
          tokens.push(`${platformId}.${group.id}:${choiceId}`);
        }
      }
    }
    return tokens.join(',');
  }

  private platformIdsForPath(
    pathname: string,
    overviewPlatformIds: PlatformId[],
  ): PlatformId[] {
    if (this.isOverviewPath(pathname)) {
      return getAvailablePricingConfigurations().map((platform) => platform.id);
    }

    const versusPlatformIds = getVersusPlatformIdsForPath(pathname);
    if (versusPlatformIds) return versusPlatformIds;

    const detailPlatform = getAvailablePricingConfigurations().find(
      (platform) => platform.detailPagePath === pathname,
    );
    return detailPlatform ? [detailPlatform.id] : overviewPlatformIds;
  }

  private isOverviewPath(pathname: string): boolean {
    return (
      pathname === '/fundraising-platforms/' ||
      pathname === '/fundraising-platforms'
    );
  }

  private matchesDefaultComparison(platformIds: PlatformId[]): boolean {
    const defaults = FUNDRAISING_PRICING_REGISTRY.defaultComparisonPlatformIds;
    return (
      platformIds.length === defaults.length &&
      platformIds.every((platformId, index) => platformId === defaults[index])
    );
  }

  private applyPlatformAliases(
    params: URLSearchParams,
    selections: ComparisonOptionSelections,
  ): void {
    if (typeof window === 'undefined') return;

    if (window.location.pathname === '/fundraising-platforms/givebacks/') {
      const model = params.get('model');
      if (model === 'tip' || model === 'percent') {
        selections.givebacks = {
          ...selections.givebacks,
          'pricing-model': model,
        };
      }
    }

    if (window.location.pathname === '/fundraising-platforms/apex/') {
      const cost = params.get('cost');
      if (cost === '30' || cost === '40' || cost === '50') {
        this.applyLegacyApexCost(selections, Number(cost));
      }
    }
  }

  private applyLegacyApexCost(
    selections: ComparisonOptionSelections,
    costPercentage: number,
  ): void {
    selections.apex = {
      ...selections.apex,
      'pricing-source': 'school-proposal',
      'organization-retained-percentage': String((100 - costPercentage) * 100),
    };
  }

  private normalizeInputOption(
    group: NonNullable<
      ReturnType<typeof getPricingConfiguration>
    >['optionGroups'][number],
    rawValue: string,
  ): string | null {
    const input = group.input;
    const value = Number(rawValue);
    if (!input || !Number.isInteger(value)) return null;
    if (
      value < input.minimumBasisPoints ||
      value > input.maximumBasisPoints ||
      (value - input.minimumBasisPoints) % input.stepBasisPoints !== 0
    ) {
      return null;
    }
    return String(value);
  }

  private formatDollarAmount(amountCents: number): string {
    const dollars = Math.floor(amountCents / 100);
    const cents = amountCents % 100;
    return cents === 0
      ? String(dollars)
      : `${dollars}.${String(cents).padStart(2, '0')}`;
  }
}
